import { isNumeric } from 'dpl/shared/form/utils';

export const SIMILAR_BREEDS_FILTER_VARIANTS = {
  CONTROL: 'control',
  VARIANT: 'variant'
} as const;

export type SimilarBreedsFilterVariant =
  (typeof SIMILAR_BREEDS_FILTER_VARIANTS)[keyof typeof SIMILAR_BREEDS_FILTER_VARIANTS];

// The module is always puppies; only the "See all" tabs move this.
export const SIMILAR_BREEDS_RESULTS_TYPES = {
  PUPPIES: 'puppies',
  BREEDERS: 'breeders'
} as const;

export type SimilarBreedsResultsType =
  (typeof SIMILAR_BREEDS_RESULTS_TYPES)[keyof typeof SIMILAR_BREEDS_RESULTS_TYPES];

// Sent explicitly rather than left to the endpoint's own default, so the
// paginator and the server agree on page size without either guessing.
// `SEE_ALL_RESULTS_PER_PAGE` in the controller is the same number.
export const SEE_ALL_RESULTS_PER_PAGE = 40;

// Fixed, and deliberately not the visitor's distance filter: this thresholds
// the main feed to pick a layout tier, so a URL param must not move it.
export const NEARBY_BREEDER_RADIUS_IN_MILES = 250;

// Distinct breeders in radius among the results served - present in the
// payload, not scrolled past. A breeder row is itself the breeder profile; a
// puppy row's own `id` is the dog's.
export function countNearbyBreeders(results: any[]): number {
  const breederProfileIds = new Set<number>();

  (results || []).forEach(result => {
    const breederProfileId = result?.breeder_profile?.id || result?.id;

    // `isNumeric` and not a null check: `'' <= 250` is true in JS, so a blank
    // distance would count and push the tier up.
    if (
      breederProfileId &&
      isNumeric(result.distance_in_miles) &&
      result.distance_in_miles <= NEARBY_BREEDER_RADIUS_IN_MILES
    ) {
      breederProfileIds.add(breederProfileId);
    }
  });

  return breederProfileIds.size;
}

/**
 * The distinct breeds a set of rows is listed under, in the order they appear.
 *
 * Deliberately the breeds *shown*, not the full related breed set the finder
 * draws from: that set is fixed per searched breed and reconstructable from the
 * breed relations, while what a given visitor was actually served is not
 * recoverable after the fact.
 *
 * `excludeBreedId` leaves the searched breed out of a mixed feed. Compared as
 * numbers because it reaches the page as a quoted string while a result's own
 * breed comes back from the API as a number.
 */
export function shownBreeds(results: any[], excludeBreedId?: number | string) {
  const excludedId = Number(excludeBreedId);
  const namesById = new Map<number, string>();

  (results || []).forEach(result => {
    const breed = result?.breed;

    if (
      breed?.id &&
      Number(breed.id) !== excludedId &&
      !namesById.has(breed.id)
    ) {
      namesById.set(breed.id, breed.name);
    }
  });

  return {
    ids: Array.from(namesById.keys()),
    names: Array.from(namesById.values())
  };
}
